import { useState, useEffect, useRef } from "react";
import { T, STATE } from "../../config/theme.js";
import { Btn, PhaseLogo as PauseLogo } from "../ui/index.jsx";

function useScrollReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, visible];
}

function RevealSection({ children, delay = 0, style = {} }) {
  const [ref, visible] = useScrollReveal();
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(40px)",
      transition: `opacity 0.8s cubic-bezier(0.22,1,0.36,1) ${delay}s, transform 0.8s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
      ...style
    }}>
      {children}
    </div>
  );
}

const API_BASE = import.meta.env.VITE_API_URL || "https://pause-api-production.up.railway.app";
const DEMO_API = API_BASE + "/api/v1/demo";

const STATE_STYLES = {
  STABLE: { label: "변화 낮음", color: "#22c55e", bg: "#22c55e18" },
  CHANGE: { label: "변화 발생", color: "#f59e0b", bg: "#f59e0b18" },
  VOLATILE: { label: "변동성 확대", color: "#ef4444", bg: "#ef444418" },
  CHECKNEEDED: { label: "정보 점검 필요", color: "#3b82f6", bg: "#3b82f618" },
};

// ═══════════════════════════════════════════════════════════════
// 신규 섹션: "GPT는 여기까지, 퍼즈는 내 기준으로" 대조 카드 + 검색 (방향 A)
//  - 기본: 삼성전자 예시 카드가 항상 보임 (검색 안 해도)
//  - 검색에서 종목 고르면: 카드 제목이 그 종목으로 바뀜 (개인화는 계속 Blur)
//  - 가입은 Blur 위 CTA로만 유도
// ═══════════════════════════════════════════════════════════════
function GptVsPauseDemo({ onStart }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selected, setSelected] = useState({ symbol: "005930", name: "삼성전자" });
  const searchTimer = useRef(null);

  // 자동완성 검색
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!query || query.length < 1) { setResults([]); return; }

    searchTimer.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await fetch(`${DEMO_API}/search?q=${encodeURIComponent(query)}`);
        const data = await res.json();
        setResults(data.results || []);
      } catch { setResults([]); }
      setSearchLoading(false);
    }, 300);

    return () => clearTimeout(searchTimer.current);
  }, [query]);

  // 방향 A: 종목 선택 → 카드 제목만 바뀜 (가입으로 튀지 않음)
  const selectStock = (stock) => {
    setSelected(stock);
    setQuery("");
    setResults([]);
  };

  return (
    <section style={{ padding: "20px 24px 100px", maxWidth: 640, margin: "0 auto" }}>
      <RevealSection>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ display: "inline-block", padding: "5px 14px", background: T.accent + "15", borderRadius: 20, marginBottom: 16 }}>
            <span style={{ fontSize: 12, color: T.accent, fontWeight: 700 }}>GPT로도 되지 않나요?</span>
          </div>
          <h2 style={{ fontSize: "clamp(22px, 3.5vw, 30px)", fontWeight: 800, lineHeight: 1.4, letterSpacing: "-0.01em", maxWidth: "20ch", margin: "0 auto" }}>
            종목 정보는 어디서나 봅니다.<br />
            <span style={{ color: T.accent }}>달라지는 건 '내 기준'부터예요.</span>
          </h2>
        </div>
      </RevealSection>

      {/* 검색창 (카드 위 — 내 종목 넣어보게) */}
      <RevealSection delay={0.05} style={{ position: "relative", zIndex: 50 }}>
        <div style={{ position: "relative", marginBottom: 20 }}>
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="내 종목을 검색해보세요 (예: 삼성전자)"
            style={{
              width: "100%", padding: "15px 20px", background: T.card,
              border: `1px solid ${T.border}`, borderRadius: 14,
              color: T.text, fontSize: 15, outline: "none", boxSizing: "border-box",
              transition: "border-color 0.2s",
            }}
            onFocus={e => e.target.style.borderColor = T.accent + "60"}
            onBlur={e => e.target.style.borderColor = T.border}
          />
          {searchLoading && (
            <div style={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: T.textMuted }}>검색 중...</div>
          )}

          {results.length > 0 && (
            <div style={{
              position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
              background: T.card, border: `1px solid ${T.border}`, borderRadius: 12,
              marginTop: 4, overflow: "hidden", boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            }}>
              {results.map((r, i) => (
                <div
                  key={i}
                  onClick={() => selectStock(r)}
                  style={{
                    padding: "12px 20px", cursor: "pointer", fontSize: 14, color: T.text,
                    borderBottom: i < results.length - 1 ? `1px solid ${T.border}` : "none",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = T.accent + "10"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <span style={{ fontWeight: 600 }}>{r.name}</span>
                  <span style={{ fontSize: 12, color: T.textMuted }}>{r.symbol}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </RevealSection>

      {/* 대조 카드 (선택된 종목 기준) */}
      <RevealSection delay={0.1} style={{ position: "relative", zIndex: 1 }}>
        <div style={{
          background: T.card,
          borderRadius: 16,
          border: `1px solid ${T.border}`,
          overflow: "hidden",
          marginBottom: 24,
        }}>
          {/* 종목 헤더 */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px 16px", borderBottom: `1px solid ${T.border}` }}>
            <div>
              <div style={{ fontSize: 11, color: T.textMuted, fontFamily: "'JetBrains Mono', monospace", marginBottom: 4 }}>
                EXAMPLE · 예시 화면
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>{selected.name}</div>
            </div>
            <div style={{ padding: "5px 14px", borderRadius: 20, fontSize: 13, fontWeight: 700, background: "#f59e0b18", color: "#f59e0b", border: "1px solid #f59e0b30" }}>
              변화 발생
            </div>
          </div>

          {/* 1층: 일반 정보 (GPT도 보여주는 것) */}
          <div style={{ padding: "20px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: 11, color: T.textMuted, fontWeight: 600, letterSpacing: "0.03em" }}>
                여기까지는 어디서나 볼 수 있어요
              </span>
              <div style={{ flex: 1, height: 1, background: T.border }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {[
                "최근 거래량이 평소보다 늘었습니다",
                "외국인 수급에 변화가 보입니다",
                "단기 변동성이 확대됐습니다",
              ].map((t, i) => (
                <div key={i} style={{ fontSize: 14, color: T.textDim, display: "flex", alignItems: "start", gap: 8 }}>
                  <span style={{ color: T.textMuted, fontSize: 8, marginTop: 6 }}>●</span> {t}
                </div>
              ))}
            </div>
          </div>

          {/* 경계선 카피 */}
          <div style={{ padding: "16px 24px", background: "rgba(255,255,255,0.02)", borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}` }}>
            <p style={{ margin: 0, fontSize: 14, color: T.text, lineHeight: 1.6, fontWeight: 500 }}>
              그런데 이 변화가 <span style={{ color: T.accent, fontWeight: 700 }}>'나에게'</span> 중요한지는,
              <br />내가 얼마에 샀고 왜 샀는지에 따라 달라집니다.
            </p>
          </div>

          {/* 2층: 개인화 영역 (Blur — 퍼즈만의 것) */}
          <div style={{ position: "relative", padding: "20px 24px" }}>
            <div style={{ filter: "blur(5px)", opacity: 0.5, pointerEvents: "none", userSelect: "none" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {[
                  "내 평단가 기준으로 이 변화가 중요한지",
                  "처음 매수한 이유와 지금이 어긋났는지",
                  "오늘 확인할 것 / 지나쳐도 될 것",
                ].map((t, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 24, height: 24, borderRadius: 6, background: T.accent + "20", flexShrink: 0 }} />
                    <div style={{ fontSize: 14, color: T.text }}>{t}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{
              position: "absolute", inset: 0,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              gap: 12, padding: "0 24px", textAlign: "center",
            }}>
              <div style={{ fontSize: 22 }}>🔒</div>
              <p style={{ margin: 0, fontSize: 15, color: T.textDim, lineHeight: 1.6 }}>
                내 평단가·수량·매수 이유를 넣으면<br />
                <span style={{ color: T.text, fontWeight: 600 }}>이 변화를 내 기준으로 다시 정리합니다</span>
              </p>
            </div>
          </div>
        </div>
      </RevealSection>

      {/* CTA */}
      <RevealSection delay={0.15}>
        <div style={{ textAlign: "center" }}>
          <button onClick={onStart} className="pause-cta" style={{
            display: "inline-block", padding: "18px 48px",
            background: T.accent, color: "#ffffff",
            fontSize: 16, fontWeight: 700, border: "none", borderRadius: 12,
            cursor: "pointer", letterSpacing: "-0.01em",
            boxShadow: `0 8px 24px ${T.accent}40`,
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
            fontFamily: "inherit",
          }}>
            내 기준으로 다시 보기
          </button>
          <p style={{ fontSize: 13, color: T.textMuted, marginTop: 12, opacity: 0.75 }}>
            30일 Core 무료 · 카드 등록 없음
          </p>
          <p style={{ fontSize: 12, color: T.textMuted, marginTop: 16, opacity: 0.6, lineHeight: 1.6 }}>
            가입하면 종목과 평단가를 등록해<br />
            실제 내 기준으로 상세 분석까지 받을 수 있어요
          </p>
        </div>
      </RevealSection>
    </section>
  );
}

export default function Landing({ onStart, onLogin, onTerms, onPrivacy, onDisclaimer, onFAQ }) {
  const [navSolid, setNavSolid] = useState(false);

  useEffect(() => {
    const handler = () => setNavSolid(window.scrollY > 60);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <div style={{ background: T.bg, color: T.text, fontFamily: "'Pretendard', sans-serif", wordBreak: "keep-all" }}>
      <link href="https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/static/pretendard.min.css" rel="stylesheet" />
      <style>{`
        /* ═══ 딥리서치 기반 디자인 토큰 (낮은 VC + 높은 PT) ═══ */
        :root {
          /* 유체 타이포그래피 - clamp(min, preferred, max) - v2 확대 */
          --fs-hero: clamp(32px, 5.5vw, 60px);
          --fs-h2:   clamp(24px, 4vw, 42px);
          --fs-h3:   clamp(19px, 2.8vw, 26px);
          --fs-body: clamp(16px, 2.1vw, 18px);
          --fs-small:clamp(14px, 1.8vw, 16px);

          /* 8pt 그리드 기반 간격 시스템 */
          --space-1: 4px;
          --space-2: 8px;
          --space-3: 16px;
          --space-4: 24px;
          --space-5: 40px;
          --space-6: 64px;
          --space-7: 96px;
          --space-8: 128px;
        }

        /* 폰트 잉여 여백(half-leading) 통제 — 지원 브라우저만 */
        @supports (text-box-trim: trim-both) {
          h1, h2, h3, h4 {
            text-box-trim: trim-both;
            text-box-edge: cap alphabetic;
          }
        }

        /* 매달린 구두점 — 한글에서도 마침표/쉼표/괄호에 효과 */
        p, blockquote, h1, h2, h3 {
          hanging-punctuation: first last allow-end;
        }

        /* 모든 텍스트 한글 줄바꿈 자연스럽게 */
        body, h1, h2, h3, h4, p {
          word-break: keep-all;
          overflow-wrap: break-word;
        }

        /* 본문 가독성 — 한글 기준 약 30~35자 한 줄 */
        .pause-prose {
          max-width: 36em;
          line-height: 1.7;
        }

        /* 모바일 반응형 (기존) */
        @media (max-width: 768px) {
          .phase-landing-grid { grid-template-columns: 1fr !important; gap: 16px !important; }
          .phase-landing-states { grid-template-columns: 1fr !important; gap: 16px !important; }
          .phase-landing-grid > div > div > div { padding: 28px 20px !important; }
        }
        @media (max-width: 480px) {
          .phase-landing-states { grid-template-columns: 1fr !important; }
        }

        /* CTA 호버 - 광학적 살짝 위로 */
        .pause-cta:hover {
          transform: translateY(-1px);
          box-shadow: 0 12px 32px var(--cta-shadow, rgba(31,107,92,0.5));
        }
      `}</style>

      {/* ─── Fixed Top Nav ─── */}
      <nav style={{
        position: "fixed", top: 0, left: 0, width: "100%", zIndex: 100,
        background: navSolid ? "rgba(8,8,13,0.92)" : "transparent",
        backdropFilter: navSolid ? "blur(16px)" : "none",
        borderBottom: navSolid ? `1px solid ${T.border}` : "1px solid transparent",
        transition: "all 0.3s ease",
      }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "14px 28px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <PauseLogo size={28} />
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <Btn variant="ghost" style={{ fontSize: 13, padding: "8px 20px", borderRadius: 8 }} onClick={onLogin}>로그인</Btn>
            <Btn variant="primary" style={{ fontSize: 13, padding: "8px 20px", borderRadius: 8 }} onClick={onStart}>무료로 시작하기</Btn>
          </div>
        </div>
      </nav>

      {/* ─── Section 1: Hero (광고 컨버전 타깃) ─── */}
      <section style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        paddingTop: 120,
        paddingBottom: 80,
      }}>
        {/* Background glow - 절제된 단일 그라데이션만 (VC 최소화) */}
        <div style={{
          position: "absolute",
          top: "-30%",
          left: "50%",
          transform: "translateX(-50%)",
          width: "100%",
          height: "80%",
          background: `radial-gradient(ellipse at 50% 40%, ${T.accent}10 0%, transparent 65%)`,
          pointerEvents: "none",
        }} />

        <div style={{
          position: "relative",
          textAlign: "center",
          maxWidth: 880,
          padding: "0 24px",
          zIndex: 2,
          width: "100%",
        }}>
          {/* 로고 - 작고 절제됨 */}
          <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            marginBottom: "var(--space-6)",
            opacity: 0.9,
          }}>
            <svg width="16" height="20" viewBox="0 0 16 20" fill="none">
              <rect x="0" y="0" width="6" height="20" rx="2" fill={T.accent} />
              <rect x="10" y="0" width="6" height="20" rx="2" fill={T.accent} />
            </svg>
            <span style={{
              fontSize: 18,
              color: T.accent,
              fontWeight: 800,
              letterSpacing: "1.5px",
              textTransform: "uppercase",
            }}>Pause</span>
          </div>

          {/* 페인 포인트 헤드라인 - 단일 블록, 강한 위계 */}
          <h1 style={{
            fontSize: "var(--fs-hero)",
            fontWeight: 800,
            lineHeight: 1.3,
            letterSpacing: "-0.02em",
            marginBottom: "var(--space-4)",
            maxWidth: "20ch",
            marginLeft: "auto",
            marginRight: "auto",
          }}>
            정보는 쏟아지는데,<br />
            <span style={{ color: T.accent }}>판단은 더 어렵습니다</span>
          </h1>

          <p style={{
            fontSize: "var(--fs-body)",
            color: T.textDim,
            lineHeight: 1.7,
            fontWeight: 400,
            maxWidth: "26ch",
            marginLeft: "auto",
            marginRight: "auto",
            marginBottom: "var(--space-6)",
          }}>
            Pause는 내가 산 이유를 기억하고,<br />
            오늘의 정보를 그 기준에 비춰 정리합니다.
          </p>

          {/* 미션 한 줄 압축 - Pause 약속 */}
          <p style={{
            fontSize: "var(--fs-body)",
            color: T.text,
            lineHeight: 1.7,
            fontWeight: 500,
            maxWidth: "30ch",
            marginLeft: "auto",
            marginRight: "auto",
            marginBottom: "var(--space-7)",
          }}>
            <span style={{ color: T.accent, fontWeight: 700 }}>매수·매도를 권하지 않습니다.</span><br />
            <span style={{ color: T.textDim }}>판단은 사용자가 직접 합니다.</span>
          </p>

          {/* 미니 시연 - Hero 허전함 해결 */}
          <div style={{
            display: "flex",
            justifyContent: "center",
            gap: 8,
            flexWrap: "wrap",
            maxWidth: 560,
            margin: "0 auto var(--space-6)",
          }}>
            {[
              "가격 위치",
              "거래량 변화",
              "수급 흐름",
              "변동성",
              "내 기준과의 거리",
            ].map((label, i) => (
              <div key={i} style={{
                padding: "8px 14px",
                background: "rgba(255,255,255,0.04)",
                borderRadius: 20,
                border: "1px solid rgba(255,255,255,0.08)",
                fontSize: 13,
                color: T.textDim,
                fontWeight: 500,
              }}>
                {label}
              </div>
            ))}
          </div>
          <p style={{
            fontSize: 13,
            color: T.textMuted,
            marginBottom: "var(--space-6)",
            opacity: 0.7,
          }}>
            5가지 관점으로 종목을 정리합니다
          </p>

          {/* CTA - 강한 광학적 강조 (흑백 대비 + 그림자) */}
          <button onClick={onStart} className="pause-cta" style={{
            display: "inline-block",
            padding: "20px 56px",
            background: T.accent,
            color: "#ffffff",
            fontSize: 17,
            fontWeight: 700,
            border: "none",
            borderRadius: 12,
            cursor: "pointer",
            letterSpacing: "-0.01em",
            boxShadow: `0 8px 24px ${T.accent}40`,
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
            fontFamily: "inherit",
          }}>
            무료로 시작
          </button>
          <p style={{
            fontSize: "var(--fs-small)",
            color: T.textMuted,
            marginTop: "var(--space-3)",
            opacity: 0.75,
            fontWeight: 400,
          }}>
            30일 무료 · 카드 등록 없이 시작
          </p>

          {/* 스크롤 유도 미세 신호 */}
          <div style={{
            marginTop: "var(--space-7)",
            fontSize: 12,
            color: T.textMuted,
            opacity: 0.45,
            letterSpacing: "0.05em",
          }}>
            ↓ 지금 우리의 투자 모습
          </div>
        </div>
      </section>

      {/* ─── 신규: GPT vs 퍼즈 대조 카드 + 검색 ─── */}
      <GptVsPauseDemo onStart={onStart} />

      {/* ─── Bridge: 스토리텔링 다리 (지금 우리 모습 → Pause로) ─── */}
      <section style={{
        padding: "80px 24px 80px",
        maxWidth: 820,
        margin: "0 auto",
        position: "relative",
      }}>
        {/* 상단 미세한 구분선 */}
        <div style={{
          position: "absolute",
          top: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: 80,
          height: 1,
          background: `linear-gradient(90deg, transparent, ${T.border}, transparent)`,
        }} />

        {/* SVG 영역 - 흩어진 점 → 모이는 흐름 (수직 레이아웃) */}
        <RevealSection delay={0.05}>
          <div style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "var(--space-6)",
          }}>
            <svg viewBox="0 0 480 320" style={{ width: "100%", maxWidth: 640, height: "auto" }} xmlns="http://www.w3.org/2000/svg">
              {/* 상단 라벨 */}
              <text x="240" y="22" textAnchor="middle" fill={T.textMuted} fontSize="13" fontWeight="600" letterSpacing="1.5">
                흩어진 정보 · 불안한 매매
              </text>

              {/* 위쪽 영역: 흩어진 점들 (혼란) */}
              <g opacity="0.6">
                <circle cx="60" cy="55" r="3.5" fill={T.textDim} opacity="0.5" />
                <circle cx="130" cy="45" r="3" fill={T.textDim} opacity="0.6" />
                <circle cx="200" cy="65" r="3.5" fill={T.textDim} opacity="0.4" />
                <circle cx="280" cy="50" r="2.5" fill={T.textDim} opacity="0.7" />
                <circle cx="360" cy="60" r="3.5" fill={T.textDim} opacity="0.5" />
                <circle cx="420" cy="45" r="2.5" fill={T.textDim} opacity="0.6" />
                <circle cx="30" cy="85" r="3" fill={T.textDim} opacity="0.5" />
                <circle cx="100" cy="95" r="2.5" fill={T.textDim} opacity="0.6" />
                <circle cx="170" cy="100" r="3.5" fill={T.textDim} opacity="0.4" />
                <circle cx="240" cy="85" r="2.5" fill={T.textDim} opacity="0.7" />
                <circle cx="320" cy="100" r="3" fill={T.textDim} opacity="0.5" />
                <circle cx="400" cy="90" r="2.5" fill={T.textDim} opacity="0.6" />
                <circle cx="70" cy="120" r="2.5" fill={T.textDim} opacity="0.5" />
                <circle cx="150" cy="130" r="3" fill={T.textDim} opacity="0.6" />
                <circle cx="225" cy="125" r="2.5" fill={T.textDim} opacity="0.4" />
                <circle cx="305" cy="135" r="3.5" fill={T.textDim} opacity="0.5" />
                <circle cx="380" cy="120" r="2.5" fill={T.textDim} opacity="0.6" />
              </g>

              {/* 중간 영역: 깔때기 흐름 (수렴) */}
              <g opacity="0.5">
                <path d="M 40 165 Q 140 185 240 195" stroke={T.accent} strokeWidth="1" fill="none" opacity="0.4" />
                <path d="M 440 165 Q 340 185 240 195" stroke={T.accent} strokeWidth="1" fill="none" opacity="0.4" />
                <path d="M 110 165 Q 175 185 240 195" stroke={T.accent} strokeWidth="1" fill="none" opacity="0.5" />
                <path d="M 370 165 Q 305 185 240 195" stroke={T.accent} strokeWidth="1" fill="none" opacity="0.5" />
                <path d="M 180 165 Q 210 185 240 195" stroke={T.accent} strokeWidth="1" fill="none" opacity="0.6" />
                <path d="M 300 165 Q 270 185 240 195" stroke={T.accent} strokeWidth="1" fill="none" opacity="0.6" />
              </g>

              {/* 중간 라벨 - Pause */}
              <text x="240" y="218" textAnchor="middle" fill={T.accent} fontSize="13" fontWeight="700" letterSpacing="1.5">
                PAUSE가 정리
              </text>

              {/* 수렴점 */}
              <circle cx="240" cy="195" r="6" fill={T.accent} opacity="0.9" />
              <circle cx="240" cy="195" r="13" fill="none" stroke={T.accent} strokeWidth="1" opacity="0.3" />

              {/* 아래쪽 영역: 한 줄로 정리된 점들 (질서) */}
              <g>
                <line x1="80" y1="265" x2="400" y2="265" stroke={T.accent} strokeWidth="1" opacity="0.3" />
                <circle cx="110" cy="265" r="3.5" fill={T.accent} opacity="0.8" />
                <circle cx="170" cy="265" r="3.5" fill={T.accent} opacity="0.8" />
                <circle cx="240" cy="265" r="4" fill={T.accent} />
                <circle cx="310" cy="265" r="3.5" fill={T.accent} opacity="0.8" />
                <circle cx="370" cy="265" r="3.5" fill={T.accent} opacity="0.8" />
              </g>

              {/* 아래쪽 보조 라인 */}
              <line x1="100" y1="285" x2="380" y2="285" stroke={T.textDim} strokeWidth="0.6" opacity="0.2" />
              <line x1="125" y1="298" x2="355" y2="298" stroke={T.textDim} strokeWidth="0.6" opacity="0.15" />

              {/* 하단 라벨 */}
              <text x="240" y="316" textAnchor="middle" fill={T.textMuted} fontSize="13" fontWeight="600" letterSpacing="1.5">
                근거 있는 결정 · 오래 가는 투자
              </text>
            </svg>
          </div>
        </RevealSection>

        {/* 텍스트 카피 - 수직 중앙 정렬 */}
        <RevealSection delay={0.15}>
          <div style={{
            maxWidth: 540,
            margin: "0 auto",
            textAlign: "center",
          }}>
            <p style={{
              fontSize: "var(--fs-body)",
              color: T.textDim,
              lineHeight: 1.85,
              marginBottom: "var(--space-5)",
              fontWeight: 400,
            }}>
              매번 근거 없이 사고팔면,<br />
              답은 생기지 않아요.<br />
              <span style={{ color: T.text, fontWeight: 500 }}>
                그저 오르기만 기도하는 매매가<br />
                계속 반복됩니다.
              </span>
            </p>
            <div style={{
              maxWidth: 360,
              margin: "0 auto",
              paddingTop: "var(--space-4)",
              borderTop: `1px solid ${T.border}`,
            }}>
              <p style={{
                fontSize: "var(--fs-h3)",
                color: T.text,
                lineHeight: 1.7,
                fontWeight: 600,
                margin: 0,
                letterSpacing: "-0.01em",
              }}>
                <span style={{ color: T.accent, fontWeight: 800 }}>Pause는 그 흐름을 멈추고,</span><br />
                근거 있는 결정으로<br />
                오래 투자할 수 있게 돕습니다.
              </p>
            </div>
          </div>
        </RevealSection>
      </section>


      {/* ─── Section 2: 3단계 흐름 (흩어진 정보 → 정리 → 질문) ─── */}
      <section style={{
        padding: "120px 24px 120px",
        maxWidth: 1100,
        margin: "0 auto",
        position: "relative",
      }}>
        <RevealSection>
          <div style={{ textAlign: "center", marginBottom: "var(--space-7)" }}>
            <div style={{
              display: "inline-block",
              padding: "6px 14px",
              background: `${T.accent}15`,
              borderRadius: 20,
              marginBottom: "var(--space-4)",
              fontSize: 12,
              color: T.accent,
              fontWeight: 700,
              letterSpacing: "0.05em",
            }}>
              Pause가 하는 일
            </div>
            <h2 style={{
              fontSize: "var(--fs-h2)",
              fontWeight: 800,
              lineHeight: 1.3,
              letterSpacing: "-0.02em",
              marginBottom: "var(--space-4)",
              maxWidth: "22ch",
              marginLeft: "auto",
              marginRight: "auto",
            }}>
              흩어진 정보를<br />
              <span style={{ color: T.accent }}>나만의 결정으로 바꾸는 흐름</span>
            </h2>
            <p style={{
              fontSize: "var(--fs-body)",
              color: T.textDim,
              lineHeight: 1.7,
              maxWidth: "32ch",
              marginLeft: "auto",
              marginRight: "auto",
            }}>
              매수·매도를 알려주지 않습니다.<br />
              대신 내가 직접 결정할 수 있도록<br />
              세 단계로 도와드려요.
            </p>
          </div>
        </RevealSection>

        {/* ═══ STEP 1: 흩어진 정보 (텍스트 폭격) ═══ */}
        <RevealSection delay={0.1}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr)",
            gap: "var(--space-5)",
            maxWidth: 900,
            margin: "0 auto var(--space-7)",
          }}>
            <div style={{
              padding: "var(--space-5) var(--space-4)",
              background: "rgba(255,255,255,0.02)",
              borderRadius: 16,
              border: `1px solid ${T.border}`,
            }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: "var(--space-4)",
                paddingBottom: "var(--space-3)",
                borderBottom: `1px solid ${T.border}`,
              }}>
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: `${T.accent}15`,
                  color: T.accent,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 800,
                  fontSize: 14,
                  fontFamily: "'JetBrains Mono', monospace",
                }}>
                  01
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: "-0.01em" }}>
                    매일 쏟아지는 정보
                  </div>
                  <div style={{ fontSize: 15, color: T.textDim, marginTop: 2 }}>
                    종목 하나에도 정리해야 할 게 이렇게 많아요
                  </div>
                </div>
              </div>

              {/* 정보 폭격 - 여러 태그가 어지럽게 */}
              <div style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                marginBottom: "var(--space-3)",
              }}>
                {[
                  { text: "외국인 매매 +153억", opacity: 0.7 },
                  { text: "KOSPI -1.2%", opacity: 0.85 },
                  { text: "영업이익 YoY +15%", opacity: 0.6 },
                  { text: "RSI 72", opacity: 0.75 },
                  { text: "신용잔고 증가", opacity: 0.55 },
                  { text: "거래량 평균 1.4배", opacity: 0.8 },
                  { text: "외인 보유 12.3%", opacity: 0.65 },
                  { text: "PER 22.8", opacity: 0.7 },
                  { text: "실적 발표 D-3", opacity: 0.9 },
                  { text: "동종업종 평균 PBR 1.8", opacity: 0.5 },
                  { text: "PBR 9.92", opacity: 0.7 },
                  { text: "차트 패턴", opacity: 0.6 },
                  { text: "공시 정정", opacity: 0.7 },
                  { text: "월간 등락 +8.2%", opacity: 0.65 },
                  { text: "기관 순매수 4일 연속", opacity: 0.8 },
                  { text: "변동성 6.1%", opacity: 0.7 },
                ].map((tag, i) => (
                  <span key={i} style={{
                    padding: "6px 12px",
                    background: "rgba(255,255,255,0.04)",
                    borderRadius: 14,
                    fontSize: 12,
                    color: T.textDim,
                    fontWeight: 500,
                    opacity: tag.opacity,
                  }}>
                    {tag.text}
                  </span>
                ))}
              </div>

              <p style={{
                margin: 0,
                fontSize: 13,
                color: T.textMuted,
                fontStyle: "italic",
                opacity: 0.7,
              }}>
                무엇이 중요한지, 지금 봐야 할 게 무엇인지 알기 어려워요.
              </p>
            </div>
          </div>
        </RevealSection>

        {/* ═══ STEP 2: Pause가 정리한 화면 (종목상세 SVG) ═══ */}
        <RevealSection delay={0.2}>
          <div style={{ textAlign: "center", marginBottom: "var(--space-4)" }}>
            <div style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              marginBottom: "var(--space-3)",
            }}>
              <div style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: `${T.accent}15`,
                color: T.accent,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 800,
                fontSize: 14,
                fontFamily: "'JetBrains Mono', monospace",
              }}>
                02
              </div>
              <h3 style={{
                fontSize: 22,
                fontWeight: 800,
                letterSpacing: "-0.01em",
                margin: 0,
              }}>
                Pause가 일상 언어로 정리합니다
              </h3>
            </div>
            <p style={{
              fontSize: "var(--fs-small)",
              color: T.textDim,
              lineHeight: 1.6,
              maxWidth: "36ch",
              marginLeft: "auto",
              marginRight: "auto",
            }}>
              흩어진 데이터를 한 화면에 모아<br />
              지금 종목 상태를 한 줄로 알려드려요.
            </p>
          </div>

          <div style={{
            maxWidth: 680,
            margin: "0 auto var(--space-7)",
            padding: "var(--space-5) var(--space-4)",
            background: "linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)",
            borderRadius: 20,
            border: `1px solid ${T.border}`,
            boxShadow: `0 24px 64px ${T.accent}08`,
          }}>
            {/* 종목 헤더 */}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingBottom: "var(--space-3)",
              marginBottom: "var(--space-4)",
              borderBottom: `1px solid ${T.border}`,
            }}>
              <div>
                <div style={{
                  fontSize: 11,
                  color: T.textMuted,
                  fontFamily: "'JetBrains Mono', monospace",
                  marginBottom: 4,
                }}>
                  EXAMPLE · 예시 화면
                </div>
                <div style={{
                  fontSize: 18,
                  fontWeight: 800,
                  letterSpacing: "-0.01em",
                }}>
                  ○○종목
                </div>
              </div>
              <div style={{
                padding: "6px 12px",
                background: `${T.accent}15`,
                borderRadius: 16,
                fontSize: 12,
                color: T.accent,
                fontWeight: 700,
              }}>
                ● 변화 낮음
              </div>
            </div>

            {/* Level 1 종합 해설 */}
            <div style={{
              padding: "var(--space-3)",
              marginBottom: "var(--space-4)",
              background: "rgba(255,255,255,0.02)",
              borderRadius: 10,
              borderLeft: `3px solid ${T.accent}`,
            }}>
              <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4, fontWeight: 600 }}>
                오늘의 종합
              </div>
              <div style={{
                fontSize: 15,
                color: T.text,
                lineHeight: 1.5,
                fontWeight: 500,
              }}>
                오늘은 평소와 비슷한 흐름이에요.<br />
                <span style={{ color: T.textDim, fontWeight: 400, fontSize: 14 }}>
                  큰 변화 없이 1년 범위의 중간 부근에서 움직이고 있어요.
                </span>
              </div>
            </div>

            {/* 5개 카드 모형 */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
              gap: 8,
            }}>
              {[
                { label: "가격 위치", value: "중간", color: T.accent },
                { label: "거래량", value: "평소 수준", color: T.textDim },
                { label: "외국인 수급", value: "균형", color: T.textDim },
                { label: "변동성", value: "낮음", color: T.accent },
                { label: "이벤트", value: "없음", color: T.textDim },
              ].map((card, i) => (
                <div key={i} style={{
                  padding: "var(--space-3) 8px",
                  background: "rgba(255,255,255,0.025)",
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,0.05)",
                  textAlign: "center",
                }}>
                  <div style={{
                    fontSize: 10,
                    color: T.textMuted,
                    marginBottom: 6,
                  }}>
                    {card.label}
                  </div>
                  <div style={{
                    fontSize: 13,
                    color: card.color,
                    fontWeight: 700,
                  }}>
                    {card.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </RevealSection>

        {/* ═══ STEP 3: Q&A 채팅 화면 ═══ */}
        <RevealSection delay={0.3}>
          <div style={{ textAlign: "center", marginBottom: "var(--space-4)" }}>
            <div style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              marginBottom: "var(--space-3)",
            }}>
              <div style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: `${T.accent}15`,
                color: T.accent,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 800,
                fontSize: 14,
                fontFamily: "'JetBrains Mono', monospace",
              }}>
                03
              </div>
              <h3 style={{
                fontSize: 22,
                fontWeight: 800,
                letterSpacing: "-0.01em",
                margin: 0,
              }}>
                막힐 땐, 질문하기
              </h3>
            </div>
            <p style={{
              fontSize: "var(--fs-small)",
              color: T.textDim,
              lineHeight: 1.6,
              maxWidth: "36ch",
              marginLeft: "auto",
              marginRight: "auto",
            }}>
              일반 검색과 달라요.<br />
              내가 보유한 종목 데이터를 바탕으로 답변해드려요.
            </p>
          </div>

          <div style={{
            maxWidth: 560,
            margin: "0 auto",
            padding: "var(--space-5) var(--space-4)",
            background: "linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)",
            borderRadius: 20,
            border: `1px solid ${T.border}`,
            boxShadow: `0 24px 64px ${T.accent}08`,
          }}>
            {/* 채팅 헤더 */}
            <div style={{
              fontSize: 11,
              color: T.textMuted,
              fontFamily: "'JetBrains Mono', monospace",
              marginBottom: "var(--space-4)",
              paddingBottom: "var(--space-2)",
              borderBottom: `1px solid ${T.border}`,
            }}>
              EXAMPLE · 내 종목 질문 기능
            </div>

            {/* 사용자 질문 말풍선 (오른쪽) */}
            <div style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "var(--space-3)",
            }}>
              <div style={{
                maxWidth: "80%",
                padding: "10px 14px",
                background: T.accent,
                color: "#fff",
                borderRadius: "16px 16px 4px 16px",
                fontSize: 14,
                lineHeight: 1.5,
                fontWeight: 500,
              }}>
                이 종목 지금 들어가도 괜찮을까?
              </div>
            </div>

            {/* Pause 답변 말풍선 (왼쪽) */}
            <div style={{
              display: "flex",
              justifyContent: "flex-start",
              marginBottom: "var(--space-3)",
              alignItems: "flex-start",
              gap: 8,
            }}>
              <div style={{
                width: 28,
                height: 28,
                flexShrink: 0,
                borderRadius: 8,
                background: `${T.accent}20`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 800,
                color: T.accent,
                fontFamily: "'JetBrains Mono', monospace",
              }}>
                ‖
              </div>
              <div style={{
                maxWidth: "82%",
                padding: "12px 14px",
                background: "rgba(255,255,255,0.05)",
                borderRadius: "4px 16px 16px 16px",
                fontSize: 14,
                lineHeight: 1.6,
                color: T.text,
              }}>
                매수·매도는 알려드릴 수 없어요.<br />
                대신 지금 ○○종목의 상태를 정리하면:<br />
                <span style={{ color: T.textDim, fontSize: 13, display: "inline-block", marginTop: 6 }}>
                  · 가격은 1년 범위의 중간 부근<br />
                  · 거래량은 평소 수준<br />
                  · 외국인 수급은 균형<br />
                </span>
                <span style={{ color: T.textDim, fontSize: 13 }}>
                  처음 이 종목을 산 이유와 지금 상태를 비교해보세요. 본인의 기준이 가장 중요합니다.
                </span>
              </div>
            </div>

            {/* 사용자 두 번째 질문 */}
            <div style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "var(--space-3)",
            }}>
              <div style={{
                maxWidth: "80%",
                padding: "10px 14px",
                background: T.accent,
                color: "#fff",
                borderRadius: "16px 16px 4px 16px",
                fontSize: 14,
                lineHeight: 1.5,
                fontWeight: 500,
              }}>
                내가 처음 적어둔 매수 이유가 뭐였지?
              </div>
            </div>

            {/* Pause 답변 2 */}
            <div style={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              gap: 8,
            }}>
              <div style={{
                width: 28,
                height: 28,
                flexShrink: 0,
                borderRadius: 8,
                background: `${T.accent}20`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 800,
                color: T.accent,
                fontFamily: "'JetBrains Mono', monospace",
              }}>
                ‖
              </div>
              <div style={{
                maxWidth: "82%",
                padding: "12px 14px",
                background: "rgba(255,255,255,0.05)",
                borderRadius: "4px 16px 16px 16px",
                fontSize: 14,
                lineHeight: 1.6,
                color: T.text,
              }}>
                <span style={{ color: T.textDim, fontSize: 13 }}>
                  처음 저장하신 이유:<br />
                </span>
                "장기 보유 종목, 분기 실적 확인 후 추가 판단"
              </div>
            </div>

            {/* 입력창 (장식) */}
            <div style={{
              marginTop: "var(--space-4)",
              padding: "10px 14px",
              background: "rgba(255,255,255,0.03)",
              borderRadius: 20,
              border: `1px solid ${T.border}`,
              fontSize: 13,
              color: T.textMuted,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
              <span>궁금한 점을 물어보세요...</span>
              <span style={{ color: T.accent, fontSize: 16 }}>→</span>
            </div>
          </div>
        </RevealSection>

        {/* 시연 캡션 + 호기심 유발 */}
        <RevealSection delay={0.4}>
          <p style={{
            textAlign: "center",
            marginTop: "var(--space-7)",
            fontSize: "var(--fs-small)",
            color: T.textMuted,
            fontStyle: "italic",
            opacity: 0.6,
            marginBottom: "var(--space-3)",
          }}>
            * 모든 화면은 예시이며, 실제 화면은 입력한 종목과 사용자 기준에 따라 달라집니다.
          </p>
          <p style={{
            textAlign: "center",
            fontSize: "var(--fs-small)",
            color: T.textMuted,
            opacity: 0.7,
            letterSpacing: "0.02em",
          }}>
            ↓ 그런데 왜 Pause는 추천을 안 할까요?
          </p>
        </RevealSection>
      </section>

      {/* ─── 창업자 서사 (신뢰 장치) ─── */}
      <section style={{
        padding: "80px 24px 80px",
        maxWidth: 640,
        margin: "0 auto",
      }}>
        <RevealSection>
          <div style={{
            position: "relative",
            background: "rgba(255,255,255,0.02)",
            border: `1px solid ${T.border}`,
            borderRadius: 20,
            padding: "40px 32px",
            maxWidth: 600,
            margin: "0 auto",
          }}>
            {/* 좌측 세로 accent 바 */}
            <div style={{
              position: "absolute",
              left: 0,
              top: 32,
              bottom: 32,
              width: 3,
              background: T.accent,
              borderRadius: 2,
              opacity: 0.6,
            }} />

            <h2 style={{
              fontSize: "var(--fs-h3)",
              fontWeight: 800,
              lineHeight: 1.5,
              letterSpacing: "-0.01em",
              marginBottom: "var(--space-5)",
              color: T.text,
            }}>
              왜 추천해주는 앱이 아니라,<br />
              <span style={{ color: T.accent, fontWeight: 800 }}>멈춰 세우는 앱</span>을 만들었을까요?
            </h2>

            <div style={{
              fontSize: 15,
              lineHeight: 1.8,
              color: T.textDim,
              fontWeight: 400,
            }}>
              <p style={{ marginBottom: "var(--space-4)" }}>
                저도 똑같이 불안했기 때문입니다.<br />
                차트를 새로고침하면서 "이걸 사야 하나, 팔아야 하나" 고민했지만,
                답을 주는 곳은 많아도 그 답이 '내 기준'에 맞는지는 아무도 말해주지 않았습니다.
              </p>

              <p style={{ marginBottom: "var(--space-4)" }}>
                한번은 고점에 물려 2년을 버틴 끝에 빠져나왔지만,
                그게 좋은 판단이었는지는 지금도 모르겠습니다.
                제게 없었던 건 수익이 아니라 <span style={{ color: T.accent, fontWeight: 700 }}>'판단의 기준'</span>이었습니다.
              </p>

              <p style={{ marginBottom: "var(--space-4)" }}>
                그래서 감에 기대는 뇌동매매나 출처 없는 조언이 아니라,
                데이터로 내 상태를 정리해주는 도구를 직접 만들었습니다.
              </p>

              <p style={{ marginBottom: "var(--space-4)" }}>
                <span style={{ color: T.text, fontWeight: 700 }}>Pause는 수익을 약속하지 않습니다.</span><br />
                대신 내 평단가와 매수 이유를 기준으로,
                지금 보는 정보가 나에게 중요한지 정리합니다.
                답을 주는 대신, 기준을 잃지 않게.
              </p>
            </div>

            <div style={{
              textAlign: "right",
              marginTop: "var(--space-5)",
              fontSize: 13,
              color: T.textMuted,
              fontWeight: 500,
            }}>
              — 퍼즈 창업자
            </div>
          </div>
        </RevealSection>
      </section>

      {/* ─── Section 3: 추천 의존의 함정 + Pause 미션 차별화 ─── */}
      <section style={{
        padding: "120px 24px 120px",
        maxWidth: 900,
        margin: "0 auto",
        position: "relative",
      }}>
        <div style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 1,
          background: `linear-gradient(90deg, transparent, ${T.border}, transparent)`,
        }} />

        <RevealSection>
          <div style={{ textAlign: "center", marginBottom: "var(--space-7)" }}>
            <h2 style={{
              fontSize: "var(--fs-h2)",
              fontWeight: 800,
              lineHeight: 1.3,
              letterSpacing: "-0.02em",
              marginBottom: "var(--space-4)",
              maxWidth: "20ch",
              marginLeft: "auto",
              marginRight: "auto",
            }}>
              누가 찍어준 종목,<br />
              <span style={{ color: T.accent }}>지금 어떻게 되고 있나요?</span>
            </h2>
            <p style={{
              fontSize: "var(--fs-body)",
              color: T.textDim,
              lineHeight: 1.8,
              maxWidth: "32ch",
              marginLeft: "auto",
              marginRight: "auto",
            }}>
              SNS에서 수익 인증 보고 들어간 종목.<br />
              유튜브에서 알려준 차트 패턴.<br />
              친구가 좋다고 한 그 회사.
            </p>
          </div>
        </RevealSection>

        <RevealSection delay={0.15}>
          <div style={{
            padding: "var(--space-6) var(--space-5)",
            background: `linear-gradient(135deg, ${T.accent}10, ${T.accent}04)`,
            borderRadius: 16,
            border: `1px solid ${T.accent}20`,
            textAlign: "center",
            marginBottom: "var(--space-5)",
          }}>
            <p style={{
              fontSize: "var(--fs-h3)",
              color: T.text,
              fontWeight: 700,
              lineHeight: 1.5,
              letterSpacing: "-0.01em",
              margin: 0,
              maxWidth: "26ch",
              marginLeft: "auto",
              marginRight: "auto",
            }}>
              결국 흔들리는 건<br />
              내 손에 들고 있는 종목이에요.
            </p>
          </div>
        </RevealSection>

        <RevealSection delay={0.25}>
          <div style={{
            textAlign: "center",
            maxWidth: "36ch",
            margin: "0 auto",
          }}>
            <p style={{
              fontSize: "var(--fs-body)",
              color: T.text,
              lineHeight: 1.8,
              fontWeight: 500,
              marginBottom: "var(--space-3)",
            }}>
              Pause는 추천하지 않습니다.<br />
              누가 찍어주는 종목이 아니라,
            </p>
            <p style={{
              fontSize: "var(--fs-body)",
              color: T.accent,
              lineHeight: 1.8,
              fontWeight: 700,
              marginBottom: "var(--space-4)",
            }}>
              내가 알고 들어간 종목으로<br />
              오래 투자할 수 있게 돕습니다.
            </p>
            <p style={{
              fontSize: "var(--fs-small)",
              color: T.textDim,
              lineHeight: 1.7,
              maxWidth: "30ch",
              marginLeft: "auto",
              marginRight: "auto",
            }}>
              하루의 가격에 흔들리지 않고,<br />
              계속 남는 기준을 먼저 봅니다.
            </p>
          </div>
        </RevealSection>
      </section>

      {/* ─── Section 4: 가격 + 표시광고법 + 최종 CTA ─── */}
      <section style={{
        padding: "120px 24px 120px",
        maxWidth: 880,
        margin: "0 auto",
        textAlign: "center",
      }}>
        <RevealSection>
          <h2 style={{
            fontSize: "var(--fs-h2)",
            fontWeight: 800,
            lineHeight: 1.3,
            letterSpacing: "-0.02em",
            marginBottom: "var(--space-4)",
            maxWidth: "24ch",
            marginLeft: "auto",
            marginRight: "auto",
          }}>
            먼저 30일 동안,<br />
            <span style={{ color: T.accent }}>Core를 무료로 사용해보세요.</span>
          </h2>
          <p style={{
            fontSize: "var(--fs-body)",
            color: T.textDim,
            lineHeight: 1.7,
            maxWidth: "32ch",
            marginLeft: "auto",
            marginRight: "auto",
            marginBottom: "var(--space-7)",
          }}>
            모든 기능을 30일 동안 자유롭게 써본 뒤,<br />
            본인에게 맞는 플랜을 선택할 수 있어요.
          </p>
        </RevealSection>

        {/* 두 플랜 카드 - Starter / Core 비교 */}
        <RevealSection delay={0.15}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: "var(--space-3)",
            maxWidth: 640,
            margin: "0 auto var(--space-6)",
          }}>
            {/* Starter */}
            <div style={{
              padding: "var(--space-5) var(--space-4)",
              background: T.card,
              borderRadius: 16,
              border: `1px solid ${T.border}`,
              textAlign: "left",
            }}>
              <div style={{
                fontSize: 12,
                color: T.textMuted,
                fontWeight: 700,
                letterSpacing: "0.08em",
                marginBottom: "var(--space-2)",
              }}>
                STARTER
              </div>
              <div style={{
                fontSize: 28,
                fontWeight: 800,
                letterSpacing: "-0.02em",
                marginBottom: 4,
                fontFamily: "'JetBrains Mono', monospace",
              }}>
                ₩9,900
                <span style={{ fontSize: 14, color: T.textDim, fontWeight: 400, fontFamily: "inherit" }}>
                  {" "}/ 월
                </span>
              </div>
              <div style={{
                fontSize: 11,
                color: T.textMuted,
                marginBottom: "var(--space-4)",
                lineHeight: 1.5,
              }}>
                VAT 별도 · 실결제 ₩10,890 · 가볍게 시작하고 싶을 때
              </div>
              <ul style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                fontSize: 13,
                color: T.textDim,
                lineHeight: 1.9,
              }}>
                <li>· 종목 3개까지</li>
                <li>· AM / PM 리포트 (1일 2회)</li>
                <li>· AI 상담 월 30회</li>
                <li>· 시장 Q&A 포함</li>
                <li>· 행동 기록 + 결과 추적</li>
              </ul>
            </div>

            {/* Core - 추천 강조 */}
            <div style={{
              padding: "var(--space-5) var(--space-4)",
              background: `linear-gradient(135deg, ${T.accent}12, ${T.accent}06)`,
              borderRadius: 16,
              border: `1.5px solid ${T.accent}50`,
              textAlign: "left",
              position: "relative",
            }}>
              <div style={{
                position: "absolute",
                top: -10,
                right: 16,
                padding: "4px 10px",
                background: T.accent,
                borderRadius: 12,
                fontSize: 10,
                color: "#fff",
                fontWeight: 700,
                letterSpacing: "0.05em",
              }}>
                추천 · 30일 무료
              </div>
              <div style={{
                fontSize: 12,
                color: T.accent,
                fontWeight: 700,
                letterSpacing: "0.08em",
                marginBottom: "var(--space-2)",
              }}>
                CORE
              </div>
              <div style={{
                fontSize: 28,
                fontWeight: 800,
                letterSpacing: "-0.02em",
                marginBottom: 4,
                fontFamily: "'JetBrains Mono', monospace",
              }}>
                ₩19,900
                <span style={{ fontSize: 14, color: T.textDim, fontWeight: 400, fontFamily: "inherit" }}>
                  {" "}/ 월
                </span>
              </div>
              <div style={{
                fontSize: 11,
                color: T.textDim,
                marginBottom: "var(--space-4)",
                lineHeight: 1.5,
              }}>
                VAT 별도 · 실결제 ₩21,890 · 모든 기능 · 충분한 분석량
              </div>
              <ul style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                fontSize: 13,
                color: T.text,
                lineHeight: 1.9,
              }}>
                <li>· 종목 10개까지</li>
                <li>· AI 상담 월 250회 (합산)</li>
                <li>· 동종 업종 비교 분석</li>
                <li>· 투자 여정 타임라인</li>
                <li>· 원칙 이탈 알림</li>
                <li>· 행동 패턴 점검</li>
              </ul>
            </div>
          </div>

          {/* ★ Sprint 26-1: Free 플랜 - Starter/Core 아래 작은 라인 ★ */}
          <div style={{
            maxWidth: 520,
            margin: "0 auto var(--space-4)",
            padding: "12px 18px",
            background: "rgba(255,255,255,0.02)",
            border: `1px solid ${T.border}`,
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <span style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.08em",
                color: T.textMuted,
                padding: "3px 8px",
                background: "rgba(255,255,255,0.04)",
                borderRadius: 6,
                flexShrink: 0,
              }}>
                FREE
              </span>
              <span style={{ fontSize: 13, color: T.textDim, lineHeight: 1.5 }}>
                <span style={{ color: T.text, fontWeight: 600 }}>0원</span>
                <span style={{ color: T.textMuted, margin: "0 6px" }}>·</span>
                종목 1개 · AI 상담 월 3회 · 기본 상태 확인
              </span>
            </div>
            <div style={{ fontSize: 11, color: T.textMuted, flexShrink: 0 }}>
              체험 종료 후 유지 가능
            </div>
          </div>

          {/* 무료 체험 흐름 안내 */}
          <div style={{
            padding: "var(--space-3) var(--space-4)",
            background: "rgba(255,255,255,0.02)",
            borderRadius: 10,
            maxWidth: 520,
            margin: "0 auto var(--space-6)",
            fontSize: 13,
            color: T.textDim,
            lineHeight: 1.6,
          }}>
            <span style={{ color: T.accent, fontWeight: 700 }}>30일 무료 체험은 Core 기준</span>으로 시작돼요.<br />
            <span style={{ fontSize: 12, color: T.textMuted }}>
              체험 종료 시 Core·Starter·Free 중 선택할 수 있어요.
            </span>
          </div>
        </RevealSection>

        {/* CTA */}
        <RevealSection delay={0.3}>
          <div style={{ marginBottom: "var(--space-5)" }}>
            <button onClick={onStart} className="pause-cta" style={{
              display: "inline-block",
              padding: "20px 56px",
              background: T.accent,
              color: "#ffffff",
              fontSize: 17,
              fontWeight: 700,
              border: "none",
              borderRadius: 12,
              cursor: "pointer",
              letterSpacing: "-0.01em",
              boxShadow: `0 8px 24px ${T.accent}40`,
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              fontFamily: "inherit",
            }}>
              무료로 시작
            </button>
            <p style={{
              fontSize: "var(--fs-small)",
              color: T.textMuted,
              marginTop: "var(--space-3)",
              opacity: 0.75,
            }}>
              30일 무료 · 카드 등록 없이 시작
            </p>
          </div>
        </RevealSection>

        {/* 표시광고법 안내 */}
        <RevealSection delay={0.4}>
          <div style={{
            padding: "var(--space-4)",
            background: "rgba(255,255,255,0.02)",
            borderRadius: 10,
            border: `1px solid ${T.border}`,
            maxWidth: 560,
            margin: "var(--space-5) auto 0",
            textAlign: "left",
          }}>
            <div style={{
              fontSize: 12,
              color: T.textMuted,
              fontWeight: 700,
              marginBottom: "var(--space-2)",
              letterSpacing: "0.03em",
            }}>
              결제 안내
            </div>
            <p style={{
              fontSize: 12.5,
              color: T.textDim,
              lineHeight: 1.7,
              margin: 0,
              fontWeight: 400,
            }}>
              30일 무료 체험은 카드 등록 없이 시작할 수 있어요. 체험 종료 시 Core(월 19,900원) 또는 Starter(월 9,900원) 중 선택하여 결제 정보를 등록하면 됩니다. 결제 후에도 마이페이지에서 언제든 플랜 변경·해지가 가능합니다. 자세한 환불 기준은 환불정책을 참고해주세요.
            </p>
          </div>
        </RevealSection>

        {/* 면책 고지 */}
        <RevealSection delay={0.45}>
          <p style={{
            fontSize: 12,
            color: T.textMuted,
            lineHeight: 1.7,
            opacity: 0.6,
            maxWidth: 520,
            margin: "var(--space-5) auto 0",
          }}>
            Pause는 매수·매도 추천이나 수익 예측을 제공하지 않습니다.<br />
            투자 판단과 책임은 투자자 본인에게 있습니다.
          </p>
        </RevealSection>
      </section>



      {/* ─── Footer ─── */}
      <footer style={{ borderTop: `1px solid ${T.border}`, padding: "32px 24px", textAlign: "center" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 12 }}>
          <div style={{ display: "flex", gap: 3 }}>
            <div style={{ width: 4, height: 16, background: T.accent, borderRadius: 2, opacity: 0.5 }} />
            <div style={{ width: 4, height: 16, background: T.accent, borderRadius: 2, opacity: 0.5 }} />
          </div>
          <span style={{ fontSize: 14, fontWeight: 700, color: T.textMuted }}>Pause</span>
        </div>
        <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 14 }}>내 원칙과 데이터를 비교해 점검하는 투자 행동 관리 도구</div>
        <div style={{ display: "flex", justifyContent: "center", gap: 20, marginBottom: 16 }}>
          <a href="https://www.instagram.com/pause_invest" target="_blank" rel="noopener noreferrer" style={{ color: T.textMuted, transition: "color 0.2s" }} onMouseEnter={e => e.currentTarget.style.color = T.accent} onMouseLeave={e => e.currentTarget.style.color = T.textMuted}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="5" />
              <circle cx="12" cy="12" r="5" />
              <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" stroke="none" />
            </svg>
          </a>
          <a href="https://www.threads.net/@pause_invest" target="_blank" rel="noopener noreferrer" style={{ color: T.textMuted, transition: "color 0.2s" }} onMouseEnter={e => e.currentTarget.style.color = T.accent} onMouseLeave={e => e.currentTarget.style.color = T.textMuted}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.084.636c-1.11-3.986-3.882-5.967-8.238-5.998h-.012c-2.853.02-5.03.94-6.465 2.735-1.39 1.737-2.095 4.176-2.1 7.253v.015c.005 3.076.71 5.512 2.1 7.247 1.435 1.79 3.61 2.71 6.468 2.73h.009c2.39-.016 4.244-.64 5.509-1.855 1.348-1.293 2.04-3.108 2.056-5.398-.012-1.163-.15-2.182-.41-3.024-.246-.8-.6-1.397-1.05-1.775-.376-.316-.822-.474-1.324-.474-.87 0-1.473.384-1.79 1.142-.234.555-.299 1.29-.194 2.184l.03.261c.158 1.353.084 2.49-.22 3.382-.345 1.014-1 1.768-1.893 2.18-.741.341-1.626.504-2.63.485-1.46-.028-2.68-.51-3.53-1.394-.842-.876-1.287-2.062-1.287-3.43 0-1.623.563-2.943 1.626-3.82.991-.817 2.348-1.27 3.822-1.274l.063.002c.862.013 1.64.17 2.312.464l.028-.742c.015-.385.03-.776.047-1.167l2.063.088c-.02.47-.04.94-.057 1.41l-.018.446c.59.37 1.085.85 1.474 1.429.582.868.923 1.94 1.012 3.187.017.242.024.493.02.75-.02 2.89-.91 5.248-2.65 7.014-1.665 1.692-4.036 2.558-7.044 2.574zM8.884 14.12c-.634.523-.973 1.289-.973 2.223 0 .876.26 1.558.772 2.09.528.547 1.296.838 2.22.855.677.014 1.257-.08 1.722-.28.42-.182.733-.478.932-.883.213-.434.299-1.065.256-1.878l-.015-.13c-.14-1.208-.04-2.268.296-3.15l.029-.072c-.443-.16-.936-.245-1.47-.253-1.052-.003-1.94.304-2.573.89l-.196.22z" />
            </svg>
          </a>
          <a href="https://blog.naver.com/pauseai" target="_blank" rel="noopener noreferrer" style={{ color: T.textMuted, transition: "color 0.2s" }} onMouseEnter={e => e.currentTarget.style.color = T.accent} onMouseLeave={e => e.currentTarget.style.color = T.textMuted}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
              <path d="M16.273 12.845 7.376 0H0v24h7.727V11.155L16.624 24H24V0h-7.727v12.845z"/>
            </svg>
          </a>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
          {[
            { label: "이용약관", fn: onTerms },
            { label: "개인정보처리방침", fn: onPrivacy },
            { label: "투자 위험 고지", fn: onDisclaimer },
            { label: "환불 정책", fn: onTerms },
            { label: "FAQ", fn: onFAQ },
          ].map((l, i) => (
            <span key={i} onClick={l.fn} style={{ fontSize: 11, color: T.textMuted, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}
              onMouseEnter={e => e.target.style.color = T.accent}
              onMouseLeave={e => e.target.style.color = T.textMuted}
            >{l.label}</span>
          ))}
        </div>
        <div style={{ fontSize: 10, color: T.textMuted, marginTop: 16, opacity: 0.4, lineHeight: 1.8 }}>
          퍼즈(Pause) | 대표 허선민 | 사업자등록번호 221-21-64135<br />
          통신판매업 제2026-부산수영-0249호<br />
          69-12, Gwangan-ro 61beon-gil, Suyeong-gu, Busan, #202<br />
          support@pause.ai.kr
        </div>
        <div style={{ fontSize: 11, color: T.textMuted, marginTop: 14, opacity: 0.5 }}>© 2026 Pause. All rights reserved.</div>
      </footer>

      <style>{`
        html { scroll-behavior: smooth; }
        body { margin: 0; overflow-x: hidden; }
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0; } }
        @media (max-width: 640px) {
          section { padding-left: 16px !important; padding-right: 16px !important; }
        }
      `}</style>
    </div>
  );
}
